import React, { useState } from 'react';
import { X, ChevronDown, ChevronRight, BookOpen, HelpCircle } from 'lucide-react';

const SECTIONS = [
  {
    id: 'what',
    title: 'What are Record Relationships?',
    content: `Record relationships let you connect two or more records that are related to each other. 
    
When records are linked, users can quickly navigate between them and optionally share data between them.

For example, if three people were in the same car accident and each has their own case, you can link those cases together so your team knows they are related and can share information like the defendant name or accident date across all three.`
  },
  {
    id: 'same-vs-cross',
    title: 'Same Type vs Cross Type Linking',
    content: `**Same type** means linking records of the same kind. For example, linking one MVA Case to another MVA Case. This is the most common use case.

**Cross type** means linking records of different kinds. For example, linking an MVA Case to a Family Law Case, or linking a Mortgage Application to a Property Inspection.

When you create a relationship type, you pick a Source Record Type and a Target Record Type. If both are the same, it is same-type linking. If they are different, it is cross-type linking.

Cross-type relationships are visible from both record types. For example, if you create a relationship from MVA Case to Family Case, it will show up in the Relationships settings of both MVA Case and Family Case.`,
    examples: [
      { industry: 'Legal', same: '3 MVA Cases for passengers in the same accident', cross: 'MVA Case linked to a Workers Comp Case from the same incident' },
      { industry: 'Lending', same: '2 Mortgage Applications from co-borrowers on the same property', cross: 'Mortgage Application linked to a Property Inspection record' },
      { industry: 'Real Estate', same: '2 Property Sales that are part of a chain (sell one, buy another)', cross: 'Property Sale linked to a Renovation Project' },
      { industry: 'Insurance', same: '3 Auto Claims from the same accident', cross: 'Auto Claim linked to a Medical Claim from the same incident' },
    ]
  },
  {
    id: 'templates',
    title: 'Relationship Templates Explained',
    subsections: [
      {
        id: 'peer',
        title: '🤝 Peer / Sibling',
        content: `Use this when records are at the same level — no record is more important than the other. They are equals linked by a shared event, person, or context.

The relationship works both ways equally. If Case A is linked to Case B, then Case B is also linked to Case A with the same label.`,
        examples: [
          { industry: 'Legal', example: 'Co-plaintiffs: 3 passengers in the same car accident, each with their own case. They share the same defendant and accident details.' },
          { industry: 'Lending', example: 'Co-borrowers: 3 people applying for a joint loan. They share the same loan terms, property, and lender.' },
          { industry: 'Real Estate', example: 'Co-buyers: A married couple each has a record for the same property purchase. They share the closing date and property address.' },
          { industry: 'Insurance', example: 'Co-claimants: Multiple people injured in the same incident, each filing their own claim against the same policy.' },
          { industry: 'Healthcare', example: 'Family members on the same insurance plan, sharing the same provider and policy number.' },
          { industry: 'HR', example: 'Sibling employees referred by the same person, sharing the referral source.' },
        ]
      },
      {
        id: 'parent_child',
        title: '👆 Parent → Child',
        content: `Use this when one record is the main record and it has smaller records underneath it. The parent record is the source of truth. Child records depend on it.

The labels are different on each side. The main record sees "Child" records listed below it. Each child record shows a link back to its "Parent."

This template automatically turns on "Lead record controls" and requires an anchor, because the parent is always the lead.`,
        examples: [
          { industry: 'Legal', example: 'Class action (parent) with hundreds of individual claims (children). The class action drives the litigation. Each claim links back to it.' },
          { industry: 'Lending', example: 'Credit facility (parent) with individual loan drawdowns (children). The facility defines the total limit. Each drawdown is a portion of it.' },
          { industry: 'Real Estate', example: 'Development project (parent) with individual unit sales (children). The project defines the location and developer. Each unit links back.' },
          { industry: 'Insurance', example: 'Master policy (parent) with individual endorsements or riders (children). The policy defines coverage. Each rider modifies it.' },
          { industry: 'Construction', example: 'Main contract (parent) with subcontracts (children). The main contract defines the scope. Each sub covers a piece of it.' },
          { industry: 'HR', example: 'Job posting (parent) with individual applications (children). The posting defines the role. Each application is a response to it.' },
        ]
      },
      {
        id: 'sequential',
        title: '➡️ Sequential',
        content: `Use this when one record comes after another in time. The first record is the predecessor, the second is the successor. They are linked because one led to the other.

This is useful for tracking history and continuity. You can see the full chain: original → first follow-up → second follow-up.`,
        examples: [
          { industry: 'Legal', example: 'Original case → Appeal. The appeal exists because of the original case. You need to see both to understand the full history.' },
          { industry: 'Lending', example: 'Original mortgage → Refinance. The refinance replaces the original loan. You need both to track the full lending history.' },
          { industry: 'Real Estate', example: 'Original contract → Amendment. The amendment changes terms of the original. Both are needed for the complete picture.' },
          { industry: 'Insurance', example: 'Original policy → Renewal. The renewal continues coverage from the original. Claims may span both periods.' },
          { industry: 'Healthcare', example: 'Initial visit → Follow-up visit. The follow-up is a continuation of the initial treatment plan.' },
          { industry: 'Government', example: 'Original filing → Amendment. The amendment updates the original filing. Both are part of the official record.' },
        ]
      },
      {
        id: 'adversarial',
        title: '⚔️ Adversarial / Opposing',
        content: `Use this when two records represent opposite sides of the same matter. They are connected but represent competing interests.

The labels are the same on both sides (both show "Opposing Party") because each side views the other as the opposition.`,
        examples: [
          { industry: 'Legal', example: 'Plaintiff case vs Defendant case. Your firm represents the plaintiff. Another team (or the same firm in different matters) handles the defendant side. Both cases are about the same dispute.' },
          { industry: 'Real Estate', example: 'Buyer transaction vs Seller transaction. Both are about the same property but from opposite perspectives with different workflows.' },
          { industry: 'Insurance', example: 'Subrogation: Your insured\'s claim vs the at-fault party\'s liability. Same incident, opposite interests.' },
          { industry: 'Lending', example: 'Lender\'s loan file vs Borrower\'s application file when both are managed in the same system.' },
          { industry: 'HR', example: 'Employee grievance vs Management response. Same workplace issue, two perspectives.' },
        ]
      },
      {
        id: 'custom',
        title: '✏️ Custom',
        content: `Use this when none of the templates above fit your situation. You start with a blank form and define everything yourself.

You choose your own names, your own sync model, and your own rules. This gives you full flexibility to create any type of connection between records.

Some examples of custom relationships that do not fit neatly into the templates above:

- A "Referred By" link between two cases that were referred by the same source
- A "Conflict Check" link between cases that have a potential conflict of interest
- A "Vendor" link between a project and a vendor record
- A "Duplicate" link between records that might be duplicates of each other`
      }
    ]
  },
  {
    id: 'sync',
    title: 'Data Sync Models Explained',
    subsections: [
      {
        id: 'anchor_controls',
        title: '🔒 Lead Record Controls',
        content: `One record is marked as the "lead" (also called the anchor). Shared fields can only be edited on the lead record. On all other linked records, those fields appear but are read-only.

When someone updates a shared field on the lead record, the change automatically pushes to all linked records.

This prevents conflicts. If 3 records are linked and 3 different people edit the same field at the same time, there is no confusion — only the lead record's value counts.`,
        when: 'Best when one record is clearly the primary or driving record. The others follow its lead.',
        examples: [
          'Co-plaintiff cases: The lead case sets the defendant name and it pushes to all linked cases',
          'Parent-child: The parent always controls shared data',
          'Syndicated loans: The lead bank sets terms, participant banks see them as read-only',
        ]
      },
      {
        id: 'free_sync',
        title: '🔄 Any Record Can Edit',
        content: `All linked records are equal. Anyone can edit a shared field on any of the linked records, and the change pushes to all others.

When you first link records, empty fields get filled from whichever record has the data. After that, any edit on any record syncs to all others.

Warning: If two people edit the same shared field on two different records at the same time, the last save wins. This can cause confusion if your team is large.`,
        when: 'Best when all records are true equals and any team member should be able to update shared information from whichever record they happen to be working on.',
        examples: [
          'Co-buyers on a property: Either buyer can update the property address',
          'Related insurance claims: Any adjuster can update the incident date',
          'Family member patients: Any record can update the shared insurance info',
        ]
      },
      {
        id: 'no_sync',
        title: '👁️ View Only — No Syncing',
        content: `Records are linked for navigation only. You can click from one record to see the other, but no data is shared or copied between them.

Each record keeps its own independent data. Changing a field on one record has zero effect on linked records.

This is the simplest and safest option. Use it when you just need to know that records are connected, without any data flowing between them.`,
        when: 'Best for loose associations where you just want easy navigation between related records. No risk of accidental data changes.',
        examples: [
          'Related cases: Just a reference link so your team knows these cases are connected',
          'Sequential records: An appeal links back to the original case for reference',
          'Conflict check: Flag that two records may have a conflict, no data sharing needed',
        ]
      }
    ]
  },
  {
    id: 'anchor',
    title: 'What is an Anchor (Lead Record)?',
    content: `The anchor is the primary or lead record in a group of linked records. Think of it as the "main" record that the others revolve around.

**When is an anchor required?**
If you choose "Lead record controls" as your sync model, an anchor is required because the system needs to know which record's data should push to the others.

For other sync models, an anchor is optional. You can still mark one record as the lead for organizational purposes, but it has no effect on data syncing.

**How does it work?**
When you link Record A to Record B, the source record (Record A) starts as the anchor. You can change the anchor later from the linked records panel on any record.

**Examples:**
- 3 co-plaintiff cases: The case with the highest damages is the anchor. Defendant info is edited there and pushes to the other two.
- Co-borrowers: The primary borrower is the anchor. Loan terms are managed on their record.
- Parent-child: The parent is always the anchor.`
  },
  {
    id: 'shared-fields',
    title: 'How Shared Fields Work',
    content: `When you create a relationship type, you can optionally select fields to share between linked records. Only fields that exist on the source record type are available for sharing.

**What happens when you share a field:**

With "Lead record controls":
- The field value on the lead record is the source of truth
- On linked records, the field shows the same value but is read-only
- When someone edits the field on the lead record, all linked records update automatically

With "Any record can edit":
- When records are first linked, empty fields get filled from whichever record has data
- After linking, editing the field on any record pushes the change to all linked records
- If both records have different values when first linked, both keep their values (no overwrite)

With "View only":
- No fields are shared. This option is not available.

**Sync Now button:**
If you change the sync model or shared fields after records are already linked, the change only affects future edits. Existing data stays as-is. Use the "Sync Now" button to preview what would change and apply it manually. You will see exactly which values will be overwritten before confirming.

**Important:** Shared fields only sync between records of the same field. If you link two different record types that have different fields, only fields that exist on both types can be effectively shared.`
  },
  {
    id: 'record-type-lock',
    title: 'Why Record Types Are Locked After Linking',
    content: `Once you create a relationship type and someone links actual records with it, you cannot change the source or target record type. The dropdowns become locked.

**Why?**
If you changed the record type after records are linked, the existing links would point to records of the wrong type. Shared fields might not exist on the new type. The data would be inconsistent and potentially broken.

**What to do if you need to change record types:**
1. Delete the relationship type (this removes all links of that type — you will be warned first)
2. Create a new relationship type with the correct record types
3. Re-link the records using the new type

**What you CAN change at any time:**
- Name and inverse name (just labels)
- Sync model (affects future behavior only)
- Shared fields (affects future syncing only)
- Color
- Anchor required setting
- Max links per record
- Description`
  },
  {
    id: 'faq',
    title: 'Frequently Asked Questions',
    subsections: [
      {
        id: 'faq1',
        title: 'Can I link a record to itself?',
        content: 'No. A record cannot be linked to itself. You can only link two different records.'
      },
      {
        id: 'faq2',
        title: 'Can I link the same two records twice?',
        content: 'Not with the same relationship type. But you can link them with different relationship types. For example, Record A and Record B can be linked as "Co-Plaintiffs" AND as "Same Accident" if those are two separate relationship types.'
      },
      {
        id: 'faq3',
        title: 'Is there a limit to how many records can be linked?',
        content: 'By default there is no limit. But when creating a relationship type, you can set a "Max links per record" number. For example, if you set it to 2 for a "Co-Signer" relationship, each record can only have 2 co-signers.'
      },
      {
        id: 'faq4',
        title: 'What happens when I delete a linked record?',
        content: 'The link is automatically removed. The other record is not affected — only the link between them is deleted.'
      },
      {
        id: 'faq5',
        title: 'Who can link records?',
        content: 'Any user who has access to both records can create a link. Only admins can create or modify relationship types.'
      },
      {
        id: 'faq6',
        title: 'Can I see all linked records across the system?',
        content: 'Each record shows its own linked records in a "Related Records" panel. There is no system-wide view of all links at this time.'
      },
    ]
  }
];

function Section({ section, depth = 0 }) {
  const [open, setOpen] = useState(depth === 0 ? false : true);

  return (
    <div className={depth === 0 ? 'border-b border-gray-100 last:border-b-0' : ''}>
      <button onClick={() => setOpen(!open)}
        className={`w-full text-left flex items-center gap-2 py-3 ${depth === 0 ? 'px-5 hover:bg-gray-50' : 'px-2 hover:bg-gray-50 rounded-lg'}`}>
        {open ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
        <span className={`font-medium ${depth === 0 ? 'text-sm text-gray-900' : 'text-xs text-gray-700'}`}>
          {section.title}
        </span>
      </button>
      {open && (
        <div className={depth === 0 ? 'px-5 pb-4' : 'px-2 pb-3'}>
          {section.content && (
            <div className="text-xs text-gray-600 leading-relaxed whitespace-pre-line ml-6">
              {section.content.split('**').map((part, i) =>
                i % 2 === 1 ? <strong key={i} className="text-gray-800">{part}</strong> : <span key={i}>{part}</span>
              )}
            </div>
          )}
          {section.when && (
            <div className="ml-6 mt-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
              <strong>When to use:</strong> {section.when}
            </div>
          )}
          {section.examples && !section.subsections && (
            <div className="ml-6 mt-2 space-y-1">
              {Array.isArray(section.examples) && typeof section.examples[0] === 'string' ? (
                section.examples.map((ex, i) => (
                  <div key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                    <span className="text-gray-300 mt-0.5">•</span> {ex}
                  </div>
                ))
              ) : (
                <div className="grid gap-1.5">
                  {section.examples.map((ex, i) => (
                    <div key={i} className="text-xs flex items-start gap-2 p-1.5 bg-gray-50 rounded">
                      <span className="font-semibold text-gray-500 min-w-[70px]">{ex.industry}</span>
                      <div>
                        {ex.example && <span className="text-gray-600">{ex.example}</span>}
                        {ex.same && <div className="text-gray-600"><strong>Same type:</strong> {ex.same}</div>}
                        {ex.cross && <div className="text-gray-600"><strong>Cross type:</strong> {ex.cross}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {section.subsections && (
            <div className="ml-4 mt-2 space-y-1">
              {section.subsections.map(sub => (
                <Section key={sub.id} section={sub} depth={depth + 1} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function RelationshipHelp({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-brand-600" />
            <h2 className="text-base font-semibold text-gray-900">Record Relationships Guide</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 bg-brand-50 border-b border-brand-100 flex-shrink-0">
          <p className="text-xs text-brand-700">
            This guide explains how record relationships work, what each template does, and how data syncing works. 
            Click on any section to expand it.
          </p>
        </div>
        <div className="overflow-y-auto flex-1">
          {SECTIONS.map(s => <Section key={s.id} section={s} />)}
        </div>
      </div>
    </div>
  );
}

export function HelpButton({ onClick }) {
  return (
    <button onClick={onClick} className="text-gray-400 hover:text-brand-600 transition-colors" title="Learn about relationships">
      <HelpCircle className="h-5 w-5" />
    </button>
  );
}
